import {PrimaryButton} from 'Components/Button';
import {Checkbox} from 'Components/Form';
import React from 'react';
import DateTime from 'luxon/src/datetime';

import AdminLayout from 'Pages/Admin/AdminLayout';

import {Loading} from 'Components/Partials';

import QuestionsApi from 'Services/Api/Admin/MainStage/Question';
import {Toast, Socket} from 'Services';
import {Link} from 'react-router-dom';

export default class Questions extends React.Component {
    state = {
        questions: [],
        meta: null,
        working: false,
        sessionId: this.props.match.params.session,
        questionsWorking: {},
        approved: {},
        approveFilter: false,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = async () => {
        await this.fetchQuestions();

        Socket.getConnection()
            .private(`main_stage_question_admin.${this.state.sessionId}`)
            .listen('MainStageQuestionAdmin', (e) => {
                if (this.state.questions.filter(q => q.id === e.question.id).length > 0) {
                    return;
                }

                this.setState({
                    questions: [
                        ...[e.question],
                        ...this.state.questions
                    ]
                });
            });

        document.addEventListener('scroll', this.loadMore);
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        document.removeEventListener('scroll', this.loadMore);

        Socket.echo.leave(`main_stage_question_admin.${this.state.sessionId}`);
    };

    /**
     * @method loadMore
     */
    loadMore = async () => {
        const bottom = document.documentElement.scrollHeight - Math.ceil(document.documentElement.scrollTop) === document.documentElement.clientHeight;

        if (bottom) {
            await this.fetchQuestions();
        }
    };

    /**
     * @method fetchReactions
     * @return {Promise<void>}
     */
    fetchQuestions = async () => {
        if (this.state.working || (this.state.meta !== null && this.state.meta?.current_page === this.state.meta?.last_page)) {
            return;
        }

        this.setState({working: true});

        const request = await QuestionsApi.get({
            session: this.state.sessionId
        }, {
            page: (this.state.meta?.current_page ?? 0) + 1,
            approved: this.state.approveFilter ? false : null
        });

        if (request.success) {
            return this.setState({
                questions: [
                    ...this.state.questions,
                    ...request.data.data
                ],
                meta: request.data.meta,
                working: false
            });
        }

        Toast.error();
        this.setState({working: false});
    };

    approveQuestion = (id) => {
        const {approved, questionsWorking} = this.state;

        if (questionsWorking[id]) {
            return;
        }

        questionsWorking[id] = true;

        this.setState({
            questionsWorking
        }, async () => {
            await QuestionsApi.patch({
                session: this.state.sessionId,
                lastResource: id
            }, {
                approved: true,
            });

            approved[id] = true;
            questionsWorking[id] = false;

            this.setState({
                approved,
                questionsWorking
            })
        })
    }

    deleteQuestion = (id, key) => {
        const {approved, questionsWorking, questions} = this.state;

        if (questionsWorking[id]) {
            return;
        }

        questionsWorking[id] = true;
        this.setState({
            questionsWorking
        }, async () => {
            await QuestionsApi.delete({
                session: this.state.sessionId,
                lastResource: id
            });

            approved[id] = true;
            questionsWorking[id] = false;

            questions.splice(key, 1);

            this.setState({
                approved,
                questionsWorking,
                questions
            })
        })
    }

    toggleFilter = () => {
        this.setState({
            approveFilter: !this.state.approveFilter,
            questions: [],
            meta: null,
        }, this.fetchQuestions);
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {working, questions, approved, questionsWorking, meta, sessionId} = this.state;

        return (
            <AdminLayout title="Main Stage - Questions">
                <div className="p-6 flex flex-col sm:flex-row">
                    <div className="mt-2 sm:mt-0 sm:ml-auto">
                        <Checkbox
                            onChange={this.toggleFilter}
                            label="Awaiting approval"
                            value={this.state.approveFilter} />
                    </div>
                </div>

                <div className="p-6">
                    { meta &&
                        <p className="font-bold mb-4">
                            Total : { meta.total }
                        </p>
                    }

                    <div className="flex space-x-2 mb-4">
                        <div className="flex flex-col items-center text-xs">
                            <div className="w-4 h-4 bg-app-light-blue"></div>
                            <span>Virtual</span>
                        </div>
                        <div className="flex flex-col items-center text-xs">
                            <div className="w-4 h-4 bg-app-blue"></div>
                            <span>In Person</span>
                        </div>
                    </div>

                    <table className="w-full table-auto">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left">User</th>
                                <th className="px-4 py-2 text-left">Question</th>
                                <th className="px-4 py-2 text-left">Created At</th>
                                <th className="px-4 py-2 text-left">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            { questions.length > 0 && (
                                <React.Fragment>
                                    { questions.map ( ( question, arrayKey ) => {
                                        const user_message_group = question.user.first_group_name ?? '';

                                        let virtual_group = user_message_group.includes ( 'virtual' );

                                        return <tr
                                            key={ question.id }
                                            className={ virtual_group ? 'bg-app-light-blue text-white' : 'bg-app-blue text-white' }
                                        >
                                            <td className="border px-3 py-2">{ question.user.full_name }</td>
                                            <td className="border px-3 py-2">{ question.question }</td>
                                            <td className="border px-3 py-2">{ question.created_at.date_time_timezone }</td>
                                            <td className="border px-3 py-2">
                                                <PrimaryButton
                                                    text="Approve"
                                                    onClick={ () => this.approveQuestion ( question.id ) }
                                                    disabled={ question.approved || approved[ question.id ] }
                                                    working={ questionsWorking[ question.id ] }
                                                />
                                                <PrimaryButton
                                                    text="Hide"
                                                    onClick={ () => this.deleteQuestion ( question.id, arrayKey ) }
                                                    working={ questionsWorking[ question.id ] }
                                                    className="ml-2"
                                                />
                                                <Link to={ `/admin/main-stage/questions/${ sessionId }/store/${ question.id }` }>
                                                    <PrimaryButton
                                                        text="View"
                                                        className="ml-2"
                                                    />
                                                </Link>
                                            </td>
                                        </tr>
                                    } ) }
                                </React.Fragment>
                            ) }

                            { questions.length === 0 && (
                                <tr>
                                    <td colSpan={ 3 }>There is no data to show you</td>
                                </tr>
                            ) }
                        </tbody>
                    </table>

                    { working && (
                        <Loading />
                    ) }
                </div>
            </AdminLayout>
        );
    }
}
